import { getIdToken } from '../Components/Auth/auth';
import {
  IDevice,
  IDeviceEvent,
  IDeviceTimelineResponse,
  IEmbeddedFirmwareListing,
  IFirmware,
  IFirmwareFileInspection,
  IFirmwareStatistic,
  IProduct,
  IProductEvent,
  IProductionFirmware,
  IProductStatistic,
  IUser,
} from './types';

export async function sendUpgradeToLatestCommand(
  productCode: number,
  serialnumbers: string[],
  variant: string,
): Promise<Result<string>> {
  const url = `/Products/${productCode}/Variants/${variant}/Devices/UpgradeToLatestRelease`;
  return await postJson<string>(url, {
    SerialNumbers: serialnumbers,
    UseBetaFirmware: false,
  });
}

export async function getFirmware(
  productCode: string,
  firmwareVersion: string,
  variant: string,
): Promise<Result<IFirmware>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/${firmwareVersion}`;
  return await get<IFirmware>(url);
}

export async function setFirmwareAsCurrent(
  productCode: string,
  variant: string,
  firmwareVersion: string,
): Promise<Result<IFirmware>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/Current`;
  return await put<IFirmware>(url, {
    firmwareVersion: firmwareVersion,
  });
}

export async function approveFirmwareByQualityAssurance(
  productCode: string,
  firmwareVersion: string,
  variant: string,
): Promise<Result<IFirmware>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/${firmwareVersion}/ApproveByQualityAssurance`;
  return await put<IFirmware>(url, {});
}

export async function getDevicesWithFirmware(
  productCode: string,
  firmwareVersion: string,
  variant: string,
): Promise<Result<IDevice[]>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/${firmwareVersion}/Devices`;
  return await get<IDevice[]>(url);
}

export async function setFirmwareAsDesired(
  productCode: number,
  serialnumbers: string[],
  firmware: number,
  variant: string,
): Promise<Result<string>> {
  const url = `/Products/${productCode}/Variants/${variant}/Devices/PrepareList`;
  return await put<string>(url, {
    SerialNumbers: serialnumbers,
    FirmwareRelease: firmware,
  });
}

export async function getDevice(
  searchTerm: string,
): Promise<Result<IDevice>> {
  const url = `/Devices/${searchTerm}`;
  return await get<IDevice>(url);
}

export async function getDeviceEvents(
  serialNumber: string,
  startTime?: string,
  endTime?: string,
): Promise<Result<IDeviceEvent[]>> {
  const query = new URLSearchParams();
  if (startTime) query.set('startTime', startTime);
  if (endTime) query.set('endTime', endTime);
  const queryString = query.toString();
  const url = `/Devices/${serialNumber}/Events${
    queryString ? `?${queryString}` : ''
  }`;
  return await get<IDeviceEvent[]>(url);
}

export async function getDeviceTimeline(
  serialNumber: string,
  startTime?: string,
  endTime?: string,
): Promise<Result<IDeviceTimelineResponse>> {
  const query = new URLSearchParams();
  if (startTime) query.set('startTime', startTime);
  if (endTime) query.set('endTime', endTime);
  const queryString = query.toString();
  const url = `/Devices/${serialNumber}/Timeline${
    queryString ? `?${queryString}` : ''
  }`;
  return await get<IDeviceTimelineResponse>(url);
}

export async function getProducts(): Promise<Result<IProduct[]>> {
  const url = `/Products`;
  return await get<IProduct[]>(url);
}

export async function getProductStatistic(
  productCode: string,
  variant: string,
): Promise<Result<IProductStatistic[]>> {
  const url = `/Products/${productCode}/Variants/${variant}/Statistics`;
  return await get<IProductStatistic[]>(url);
}

export async function getFirmwareStatistic(
  productCode: string,
  firmwareVersion: string,
  variant: string,
): Promise<Result<IFirmwareStatistic>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/${firmwareVersion}/stats`;
  return await get<IFirmwareStatistic>(url);
}

export async function getDevicesOnProduct(
  productCode: string,
  variant: string,
): Promise<Result<number>> {
  const url = `/Products/${productCode}/Variants/${variant}/Devices/Count`;
  return await get<number>(url);
}

export async function getProductEvent(
  productCode: string,
  variant: string,
): Promise<Result<IProductEvent[]>> {
  const url = `/Products/${productCode}/Variants/${variant}/Events`;
  return await get<IProductEvent[]>(url);
}

export async function postNewFirmware(
  productId: string,
  variantId: string,
  firmwareNumber: string,
  firmwareSemantic: string,
  firmware: File,
): Promise<Result<string>> {
  const formData = new FormData();
  formData.append('file', firmware);
  if (firmwareNumber) {
    formData.append('firmwareVersion', firmwareNumber);
  }
  if (firmwareSemantic) {
    formData.append('semanticVersion', firmwareSemantic);
  }
  const url = `/Products/${productId}/Variants/${variantId}/Firmwares`;
  return await postForm<string>(url, formData);
}

export async function getEmbeddedFirmwareFiles(
  productCode: string,
  variant: string,
  relativePrefix: string,
  continuationToken?: string,
): Promise<Result<IEmbeddedFirmwareListing>> {
  const query = new URLSearchParams();
  if (relativePrefix) query.set('relativePrefix', relativePrefix);
  if (continuationToken)
    query.set('continuationToken', continuationToken);
  const queryString = query.toString();
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/EmbeddedS3${
    queryString ? `?${queryString}` : ''
  }`;
  return await get<IEmbeddedFirmwareListing>(url);
}

export async function getEmbeddedFirmwareAvailability(
  productCode: string,
  variant: string,
): Promise<Result<boolean>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/EmbeddedS3/Availability`;
  return await get<boolean>(url);
}

export async function inspectEmbeddedFirmware(
  productCode: string,
  variant: string,
  sourceKey: string,
  etag: string,
): Promise<Result<IFirmwareFileInspection>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/EmbeddedS3/Inspect`;
  return await postJson<IFirmwareFileInspection>(url, {
    sourceKey,
    etag,
  });
}

export async function importEmbeddedFirmware(
  productCode: string,
  variant: string,
  sourceKey: string,
  etag: string,
  firmwareVersion: number,
  semanticVersion: string,
): Promise<Result<string>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/EmbeddedS3/Import`;
  return await postJson<string>(url, {
    sourceKey,
    etag,
    firmwareVersion,
    semanticVersion,
  });
}

export async function inspectFirmwareFile(
  productId: string,
  variantId: string,
  firmware: File,
): Promise<Result<IFirmwareFileInspection>> {
  const formData = new FormData();
  formData.append('file', firmware);
  const url = `/Products/${productId}/Variants/${variantId}/Firmwares/Inspect`;
  return await postForm<IFirmwareFileInspection>(url, formData);
}

export async function inspectStoredFirmware(
  productCode: string,
  variant: string,
  firmwareVersion: string,
): Promise<Result<IFirmwareFileInspection>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/${firmwareVersion}/Inspect`;
  return await get<IFirmwareFileInspection>(url);
}

export async function deleteFirmware(
  productId: number,
  variantId: string,
  firmwareVersion: string,
): Promise<Result<string>> {
  const url = `/Products/${productId}/Variants/${variantId}/Firmwares/${firmwareVersion}`;
  return await deleteHttp<string>(url);
}
export async function setDesiredFirmwareAndTryUpgrade(
  productCode: string,
  numberOfDevices: number,
  firmwareVersion: number,
  variant: string,
): Promise<Result<string>> {
  const url = `/Products/${productCode}/Variants/${variant}/Devices/PrepareNumberOfDevices`;
  return await put<string>(url, {
    numberOfDevices: numberOfDevices,
    firmwareVersion: firmwareVersion,
  });
}

export async function postFirmware(
  productCode: string,
  variant: string,
  formData: FormData,
): Promise<Result<string>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares`;
  return await postForm<string>(url, formData);
}

export async function postUpgradeDevices(): Promise<Result<string>> {
  const url = `/upgrades/devices`;
  return await postJson<string>(url, null);
}

export async function postUpgradeModems(): Promise<Result<string>> {
  const url = `/upgrades/modems`;
  return await postJson<string>(url, null);
}

export async function getUsers(): Promise<Result<IUser[]>> {
  const url = `/Users`;
  return await get<IUser[]>(url);
}

export const isOk = <T>(data: Result<T>): data is OK<T> => {
  return data.status === 'OK';
};

export type OK<T> = {
  status: 'OK';
  data: T;
};

export type ERROR = {
  status: 'Error';
  errorMessages: string[];
};

export type NOT_FOUND = {
  status: 'Not Found';
  errorMessages: string[];
};

export type LOADING = {
  status: 'Loading';
};

export type NETWORK_ISSUE = {
  status: 'Network issue';
  errorMessages: string[];
};

export type FORBIDDEN = {
  status: 'Forbidden';
  errorMessages: string[];
};

export type DEFAULT = {
  status: '';
};
export type Result<T> =
  | OK<T>
  | ERROR
  | LOADING
  | DEFAULT
  | NOT_FOUND
  | NETWORK_ISSUE
  | FORBIDDEN;

const formDataOptions = async (method: 'POST' | 'PUT') => ({
  method: method,
  headers: {
    Accept: 'multipart/form-data',
    Authorization: `Bearer ${await getIdToken()}`,
    From: 'firmware-manager-app',
  },
});

const jsonOptions = async (
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
) => ({
  method: method,
  headers: {
    Accept: 'application/json',
    Authorization: `Bearer ${await getIdToken()}`,
    'Content-Type': 'application/json',
    From: 'firmware-manager-app',
  },
});

function handleRequestException<T>(error: any): Result<T> {
  if (
    error instanceof TypeError &&
    error.message === 'Failed to fetch'
  ) {
    return {
      status: 'Network issue',
      errorMessages: ['Blocked by firewall'],
    };
  }
  if (
    error.name === 'TimeoutError' ||
    error.message?.includes('timed out')
  ) {
    return {
      status: 'Network issue',
      errorMessages: [
        'The request took too long. Please check your connection or VPN.',
      ],
    };
  }
  if (
    error.message?.includes('NetworkError') ||
    error.message?.includes('Load failed')
  ) {
    return {
      status: 'Network issue',
      errorMessages: ['Network connection lost or blocked.'],
    };
  }
  console.error('Unexpected error during request: ', error);
  return {
    status: 'Error',
    errorMessages: [
      error.name || 'Unknown error',
      error.message || 'No details provided',
    ],
  };
}
async function get<T>(url: string): Promise<Result<T>> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${url}`,
      await jsonOptions('GET'),
    );
    if (response.status === 200) {
      const result = await response.json();
      return {
        status: 'OK',
        data: result,
      };
    }
    if (response.status === 204) {
      return {
        status: 'Error',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    if (response.status === 403) {
      return {
        status: 'Forbidden',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    if (response.status === 404) {
      return {
        status: 'Not Found',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    return {
      status: 'Error',
      errorMessages: await tryParseErrorMessage(response),
    };
  } catch (error: any) {
    return handleRequestException(error);
  }
}

async function deleteHttp<T>(url: string): Promise<Result<T>> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${url}`,
      await jsonOptions('DELETE'),
    );
    if (response.status === 200) {
      const result = await response.json();
      return {
        status: 'OK',
        data: result,
      };
    }
    if (response.status === 204) {
      return {
        status: 'Error',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    if (response.status === 403) {
      return {
        status: 'Forbidden',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    if (response.status === 404) {
      return {
        status: 'Not Found',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    return {
      status: 'Error',
      errorMessages: await tryParseErrorMessage(response),
    };
  } catch (error: any) {
    return handleRequestException(error);
  }
}

async function postForm<T>(
  url: string,
  body: FormData,
): Promise<Result<T>> {
  try {
    const postOptions = {
      ...(await formDataOptions('POST')),
      body: body,
    };
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${url}`,
      postOptions,
    );
    if (response.ok) {
      const result = await response.json();
      return {
        status: 'OK',
        data: result,
      };
    }
    if (response.status === 403) {
      return Promise.reject({
        status: 'Forbidden',
        errorMessages: await tryParseErrorMessage(response),
      });
    }
    if (response.status === 404) {
      return Promise.reject({
        status: 'Not Found',
        errorMessages: await tryParseErrorMessage(response),
      });
    }
    return Promise.reject({
      status: 'Error',
      errorMessages: await tryParseErrorMessage(response),
    });
  } catch (error: any) {
    return Promise.reject(handleRequestException(error));
  }
}

async function postJson<T>(
  url: string,
  body: any,
): Promise<Result<T>> {
  try {
    const postOptions = {
      ...(await jsonOptions('POST')),
      body: JSON.stringify(body),
    };
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${url}`,
      postOptions,
    );
    if (response.ok) {
      const result = await response.json();
      return {
        status: 'OK',
        data: result,
      };
    }
    if (response.status === 403) {
      return {
        status: 'Forbidden',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    if (response.status === 404) {
      return {
        status: 'Not Found',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    return {
      status: 'Error',
      errorMessages: await tryParseErrorMessage(response),
    };
  } catch (error: any) {
    return handleRequestException(error);
  }
}

async function put<T>(url: string, body: any): Promise<Result<T>> {
  try {
    const putOptions = {
      ...(await jsonOptions('PUT')),
      body: JSON.stringify(body),
    };
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${url}`,
      putOptions,
    );
    if (response.ok) {
      const result = await response.json();
      return {
        status: 'OK',
        data: result,
      };
    }
    if (response.status === 403) {
      return {
        status: 'Forbidden',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    if (response.status === 404) {
      return {
        status: 'Not Found',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    return {
      status: 'Error',
      errorMessages: await tryParseErrorMessage(response),
    };
  } catch (error: any) {
    return handleRequestException(error);
  }
}

const tryParseErrorMessage = async (
  response: Response,
): Promise<string[]> => {
  if (response) {
    try {
      const errorMessages = (await response.json()) as string[];
      return errorMessages;
    } catch {
      return ['Something went wrong'];
    }
  }
  return ['Something went wrong'];
};

// Device Manufacturing API functions
async function getDeviceManufacturing<T>(
  url: string,
): Promise<Result<T>> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_DEVICE_MANUFACTURING_API_URL}${url}`,
      await jsonOptions('GET'),
    );
    if (response.status === 200) {
      const result = await response.json();
      return {
        status: 'OK',
        data: result,
      };
    }
    if (response.status === 403) {
      return {
        status: 'Forbidden',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    if (response.status === 404) {
      return {
        status: 'Not Found',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    return {
      status: 'Error',
      errorMessages: await tryParseErrorMessage(response),
    };
  } catch (error: any) {
    return handleRequestException(error);
  }
}

async function putDeviceManufacturing<T>(
  url: string,
  body: any,
): Promise<Result<T>> {
  try {
    const putOptions = {
      ...(await jsonOptions('PUT')),
      body: JSON.stringify(body),
    };
    const response = await fetch(
      `${import.meta.env.VITE_DEVICE_MANUFACTURING_API_URL}${url}`,
      putOptions,
    );
    if (response.ok) {
      const result = await response.json();
      return {
        status: 'OK',
        data: result,
      };
    }
    if (response.status === 403) {
      return {
        status: 'Forbidden',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    if (response.status === 404) {
      return {
        status: 'Not Found',
        errorMessages: await tryParseErrorMessage(response),
      };
    }
    return {
      status: 'Error',
      errorMessages: await tryParseErrorMessage(response),
    };
  } catch (error: any) {
    return handleRequestException(error);
  }
}

export async function getProductionFirmware(
  productCode: number,
  variant: string,
): Promise<Result<IProductionFirmware | null>> {
  const url = `/api/production-firmware/product/${productCode}/variant/${variant}`;
  return await getDeviceManufacturing<IProductionFirmware | null>(
    url,
  );
}

export async function getCurrentFirmware(
  productCode: number,
  variant: string,
): Promise<Result<IFirmware>> {
  const url = `/Products/${productCode}/Variants/${variant}/Firmwares/Current`;
  return await get<IFirmware>(url);
}

export async function setProductionFirmware(
  productCode: number,
  variant: string,
  firmwareVersion: number,
): Promise<Result<IProductionFirmware>> {
  const url = `/api/production-firmware/product/${productCode}/variant/${variant}`;
  return await putDeviceManufacturing<IProductionFirmware>(url, {
    firmwareVersion: firmwareVersion,
  });
}
